import { cloneElement, useState } from 'react'
import { useSearchParams } from 'react-router'
import { useContactDuplicates } from '@/shared/api/leads'
import { LEAD_PARAM } from '@/shared/routes'
import { useBrand } from '@/shared/white-label/useBrand'
import { Ico } from '@/ui/Ico'
import type { ContactDuplicate } from '@/shared/types'
import { duplicateHeadline, groupDuplicates, type DuplicateGroup } from './duplicateWording'

const VISIBLE = 3

function Row({ match, onOpen }: { match: ContactDuplicate; onOpen: () => void }) {
  return (
    <li className="flex items-baseline gap-2 flex-wrap py-1 border-t border-ink-200 first:border-t-0">
      <button
        type="button"
        onClick={onOpen}
        className="text-[12px] font-semibold text-ink-1000 bg-transparent border-0 p-0 cursor-pointer font-[inherit] underline decoration-ink-300 hover:decoration-ink-1000"
        title={`Öppna ${match.company}`}
      >
        {match.company}
      </button>
      <span className="text-[11px] text-ink-500">
        {match.contact}
        {match.contactRole ? ` · ${match.contactRole}` : ''}
      </span>
      {match.orgNo && (
        <span className="text-[10px] text-ink-500 font-mono">org {match.orgNo}</span>
      )}
    </li>
  )
}

/** One warning: a contact on this lead, what matched, and only the other
 *  leads' contacts that matched it on those fields. */
function Group({
  group,
  onOpen,
}: {
  group: DuplicateGroup<ContactDuplicate>
  onOpen: (companyId: string) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const { matches } = group
  const shown = expanded ? matches : matches.slice(0, VISIBLE)
  const leadCount = new Set(matches.map(m => m.companyId)).size

  return (
    <div className="mt-2 first:mt-0">
      <div className="flex items-center gap-2 text-[12px] font-semibold text-ink-1000">
        <span className="text-warning inline-flex">
          {cloneElement(Ico.stack, { width: 13, height: 13 })}
        </span>
        {duplicateHeadline(group.ourContact, group.matchedOn, leadCount)}
      </div>
      <ul className="mt-1 list-none p-0 m-0">
        {shown.map(m => (
          <Row key={m.contactId} match={m} onOpen={() => onOpen(m.companyId)} />
        ))}
      </ul>
      {matches.length > VISIBLE && (
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="mt-1 text-[11px] text-ink-500 hover:text-ink-1000 bg-transparent border-0 p-0 cursor-pointer font-[inherit]"
        >
          {expanded ? 'Visa färre' : `Visa ${matches.length - VISIBLE} till`}
        </button>
      )}
    </div>
  )
}

/**
 * "The same human, twice" — YLLW-RADAR-101.
 *
 * Not a merge affordance. The two companies have different org numbers, so
 * they are different legal entities and both leads are legitimate; what is not
 * legitimate is calling one person twice about the same thing. So this states
 * the overlap, names what agreed, and links to the other lead.
 */
export default function ContactDuplicates({ leadId }: { leadId: string }) {
  const { data } = useContactDuplicates(leadId)
  const [params, setParams] = useSearchParams()
  const { person } = useBrand()

  const matches = data?.matches ?? []
  if (matches.length === 0) return null

  const openLead = (companyId: string) => {
    const next = new URLSearchParams(params)
    next.set(LEAD_PARAM, companyId)
    setParams(next, { replace: true })
  }

  const owners = [...new Set(matches.map(m => m.assignedTo).filter(Boolean))] as string[]

  return (
    <section
      className="mt-3 rounded-[3px] border border-warning/40 bg-warning/5 px-3 py-2"
      aria-label="Kontakter som finns på andra leads"
    >
      {groupDuplicates(matches).map(g => (
        <Group key={g.key} group={g} onOpen={openLead} />
      ))}
      <p className="mt-1.5 text-[11px] text-ink-500">
        Olika organisationsnummer, så de förblir separata leads — kontrollera
        innan du ringer, så att samma person inte blir uppringd två gånger.
        {owners.length > 0 && ` Bearbetad av ${owners.map(person).join(', ')}.`}
      </p>
    </section>
  )
}
